import { PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Divider, Form, Input, InputNumber, Row, Select, Space, Spin } from "antd";
import CustomDatePicker from "common/CustomDatePicker";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import {
  ALL_SALES_CONTRACT_LIST,
  GET_ALL_B2BLCS,
  GET_LC_ACCEPTANCE_DETAIL,
  IMPORT_DETAIL_ADD,
  IMPORT_DETAIL_ADD_DOC,
  IMPORT_DETAIL_ALL_SUPPLIERS,
  IMPORT_DETAIL_COURIER_NAMES,
  IMPORT_DETAIL_COURIER_NAMES_ADD,
  IMPORT_DETAIL_EDIT,
  SALES_CONTRACT_LIST,
} from "../../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../../apiServices/common";
import { alertPop } from "../../../../apiServices/common/helper";
import { commaSeparateNumber } from "../../../../utils/functions";
import ImportDetailDocument from "../../../Common/ImportDetailDocument";
import { LOCAL_INVOICE_DOC_SEND_RECEIVE_DATA_PERMISSION } from "../../../../routes/permissions";
import { hasPermission } from "../../../../utils/functions";

const { Option } = Select;

const AddExportSalesContractForm = forwardRef((props, ref) => {
  // Props
  const { permitInfo, view, form, setLoading, loading, supplierType, disableButton, setDisableButton, disableUpdateButton } =
    props;

  const goodsDescription = [
    { id: 1, name: "Fabric" },
    { id: 2, name: "Accessories" },
    { id: 3, name: "Fabric & Accessories" },
    { id: 4, name: "Print" },
    { id: 5, name: "Washing" },
    { id: 6, name: "Embroidery" },
    { id: 7, name: "Testing" },
    { id: 8, name: "Inspection" },
    { id: 9, name: "Courier" },
    { id: 10, name: "Portal" },
  ];

  // States
  const [b2bLcList, setB2bLcList] = useState([]);
  const [positionType, setPositionType] = useState();
  const [suppliersList, setSuppliersList] = useState([]);
  const [filesList, setFileList] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [salesContractList, setSalesContractList] = useState(null);
  const [disableField, setDisableField] = useState(false);
  const [selectedGoodsDescription, setSelectedGoodsDescription] = useState(null);
  const [hasInitialNote, setHasInitialNote] = useState(false);
  const [courierNameList, setCourierNameList] = useState([]);
  const [newCourierNameInput, setNewCourierNameInput] = useState("");
  const [addingCourierName, setAddingCourierName] = useState(false);
  const isSubmittingRef = useRef(false);

  const isFocCourierForeign = supplierType === "foreign" && selectedType === "FOC Courier";

  const addCourierName = async () => {
    const name = newCourierNameInput?.trim();
    if (!name) {
      return;
    }

    setAddingCourierName(true);
    try {
      const response = await postData(IMPORT_DETAIL_COURIER_NAMES_ADD, { name });

      if (response && response?.code === 200) {
        setCourierNameList((prev) => (prev.includes(name) ? prev : [...prev, name].sort()));
        form.setFieldValue("forwarder_name", name);
        setNewCourierNameInput("");
        alertPop("success", "Courier name saved successfully");
      } else {
        alertPop("error", response?.message || "Failed to save courier name");
      }
    } finally {
      setAddingCourierName(false);
    }
  };

  // Others
  var document = [];
  var document_name = [];

  const formatFormDate = (val) => (val ? moment(val).format("YYYY-MM-DD") : null);

  const onFinishSalesContract = async (values) => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);

    try {
      if (filesList?.length) {
        filesList.forEach((item) => {
          document_name.push(item?.name);
          document.push(item?.id);
        });
      }

      // add supplierTypeId to values
      values["supplier_type"] = supplierType;

      const bodyData = {
        ...values,
        ...(permitInfo && { id: permitInfo?.sales_contract_commercial?.id }),
        sales_contract_number: values.sales_contract_number || null,
        invoice_number: values.invoice_number?.trim() ? values.invoice_number.trim() : null,
        forwarder_name: values.forwarder_name?.trim() ? values.forwarder_name.trim() : null,
        delivery_date: formatFormDate(values.delivery_date),
        inhouse_date: formatFormDate(values.inhouse_date),
      };

      if (bodyData?.id > 0) {
        const res = await postData(IMPORT_DETAIL_EDIT + "/" + bodyData?.id, bodyData);
        if (res && res?.code === 200) {
          alertPop("success", "Daily Import Position Updated Successfully");
        }
      } else {
        const res = await postData(IMPORT_DETAIL_ADD, bodyData);
        if (res && res?.code === 200) {
          alertPop("success", "Daily Import Position Added Successfully");
        }
      }
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const getSuppliersList = useCallback(async () => {
    let response = await getData(IMPORT_DETAIL_ALL_SUPPLIERS);

    if (response && response?.status === 200) {
      setSuppliersList(response?.data?.data || []);
    } else {
      alertPop("error", response?.messages ? response?.messages : "Something Went Wrong!");
    }
  }, []);

  const getCourierNameList = useCallback(async () => {
    const response = await getData(IMPORT_DETAIL_COURIER_NAMES);

    if (response && response?.status === 200) {
      setCourierNameList(response?.data?.data || []);
    }
  }, []);

  const getSalesContractList = async () => {
    let response = await getData(ALL_SALES_CONTRACT_LIST);

    if (response && response?.status === 200) {
      setSalesContractList(response?.data?.data || []);
    } else {
      alertPop("error", response?.messages ? response?.messages : "Something Went Wrong!");
    }
  };

  const getBuyerInfo = async (salesContractId) => {
    // Use the same API endpoint for both FOC and FOC Courier
    let response = await getData(`${SALES_CONTRACT_LIST}/${salesContractId}/view`);

    if (response && response?.status === 200) {
      const salesContract = response?.data?.data?.sales_contract;
      form.setFieldValue("buyer_name", salesContract?.buyer_info?.name);

      // Only set UD Number and UD Date if selectedType is FOC or FOC Courier
      if (selectedType === "FOC" || selectedType === "FOC Courier") {
        const commercialInfo = salesContract?.sales_contract_commercial;
        if (commercialInfo) {
          form.setFieldsValue({
            udn: commercialInfo?.ud_number || null,
            ud_date: commercialInfo?.ud_date ? moment(commercialInfo.ud_date) : null,
          });
        }
      }
    } else {
      alertPop("error", response?.messages ? response?.messages : "Something Went Wrong!");
    }
  };

  const getLcNumber = async (value) => {
    form.resetFields(["import_lc_number"]);
    setPositionType(value);
    const endpoint = `${GET_ALL_B2BLCS}?payment-type=${value}&supplier-type=${supplierType}`;
    const response = await getData(endpoint);
    if (response) {
      setB2bLcList(response?.data?.data || []);
    }
  };

  const getLcDetails = async (value, fromOnChange) => {
    let get_b2blc_details = `/api/accounting/import-b2blc/view/${value}`;
    let res = await getData(get_b2blc_details);

    if (res) {
      form.setFieldsValue({
        import_date: res?.data?.data[0]?.sales_contract_commercial?.b2b_lc_date
          ? moment(res?.data?.data[0]?.sales_contract_commercial?.b2b_lc_date)
          : null,
        supplier_id: res?.data?.data[0]?.sales_contract_commercial?.supplier_id,
        buyer_name: res?.data?.data[0]?.sales_contract_commercial?.sale_contract_info?.buyer_info?.name,
        sales_contract_number: res?.data?.data[0]?.sales_contract_commercial?.sale_contract_info?.id,
        sales_contract_id: res?.data?.data[0]?.sales_contract_commercial?.sale_contract_info?.id,
        udn: res?.data?.data[0]?.sales_contract_commercial?.sales_contract_commercial_info?.ud_number,
        ud_date: res?.data?.data[0]?.sales_contract_commercial?.sales_contract_commercial_info?.ud_date
          ? moment(res?.data?.data[0]?.sales_contract_commercial?.sales_contract_commercial_info?.ud_date)
          : null,
        goods_description: res?.data?.data[0]?.sales_contract_commercial?.description,
      });
    }
  };

  const getLcDetailsEdit = async (value) => {
    let get_b2blc_details = `/api/accounting/import-b2blc/view/${value}`;
    let res = await getData(get_b2blc_details);

    if (res) {
      const ud_date = res?.data?.data[0]?.sales_contract_commercial?.sales_contract_commercial_info?.ud_date
        ? moment(res?.data?.data[0]?.sales_contract_commercial?.sales_contract_commercial_info?.ud_date)
        : null;

      form.setFieldsValue({
        udn: res?.data?.data[0]?.sales_contract_commercial?.sales_contract_commercial_info?.ud_number,
        ud_date: ud_date,
      });
    }
  };

  const isFieldDisabled = () => {
    if (supplierType === "local" && hasPermission(LOCAL_INVOICE_DOC_SEND_RECEIVE_DATA_PERMISSION)) {
      setDisableField(true);
    } else {
      setDisableField(false);
    }
  };

  useEffect(() => {
    isFieldDisabled();
  }, [supplierType]);

  // Watch goods_description form value for Local supplier type
  useEffect(() => {
    if (supplierType === "local" && form) {
      const goodsDescValue = form.getFieldValue("goods_description");
      if (goodsDescValue) {
        setSelectedGoodsDescription(goodsDescValue);
      }
    }
  }, [supplierType, form]);

  // Sync Delivery Date and Projected In-House Date for FOC Courier
  useEffect(() => {
    if (selectedType === "FOC Courier" && form) {
      const deliveryDate = form.getFieldValue("delivery_date");
      const inhouseDate = form.getFieldValue("inhouse_date");

      // Sync inhouse_date to delivery_date when delivery_date changes
      if (deliveryDate) {
        if (!inhouseDate || !deliveryDate.isSame(inhouseDate)) {
          form.setFieldValue("inhouse_date", deliveryDate);
        }
      }
    }
  }, [selectedType, form]);

  // Set default values for FOC Courier type in Foreign supplier
  useEffect(() => {
    if (supplierType === "foreign" && selectedType === "FOC Courier" && form) {
      form.setFieldsValue({
        shipment_mode: "AIR",
        loading_point: "AIRPORT",
      });
    }
  }, [supplierType, selectedType, form]);

  useEffect(() => {
    getSuppliersList();
    getSalesContractList();
    if (supplierType === "foreign") {
      getCourierNameList();
    }
  }, [getSuppliersList, getCourierNameList, supplierType]);

  useEffect(() => {
    if (permitInfo && form) {
      let sales_contract_commercial_value = permitInfo?.sales_contract_commercial;

      if (
        sales_contract_commercial_value?.position_type !== "FOC" &&
        sales_contract_commercial_value?.position_type !== "FOC Courier"
      ) {
        getLcDetailsEdit(permitInfo?.sales_contract_commercial?.import_lc_number);
        getLcNumber(sales_contract_commercial_value?.position_type);

        delete sales_contract_commercial_value["ud_date"];
        delete sales_contract_commercial_value["udn"];
      }

      if (sales_contract_commercial_value?.position_type === "LC") {
        getAcceptanceDetails({
          import_b2blc_id: sales_contract_commercial_value?.import_lc_number,
          import_detail_id: sales_contract_commercial_value?.id,
          b2blc_terms_of_payment: sales_contract_commercial_value?.b2blc_info?.terms_of_payment,
        });
      }

      setSelectedType(permitInfo?.sales_contract_commercial?.position_type);

      form.setFieldsValue({
        ...sales_contract_commercial_value,
        sales_contract_number: sales_contract_commercial_value?.sales_contract_id,
        import_lc_number: sales_contract_commercial_value.import_lc_number,
        import_date:
          sales_contract_commercial_value.import_date != null
            ? moment(sales_contract_commercial_value.import_date, "YYYY-MM-DD")
            : null,
        copy_documents_receive_date:
          sales_contract_commercial_value.copy_documents_receive_date != null
            ? moment(sales_contract_commercial_value.copy_documents_receive_date, "YYYY-MM-DD")
            : null,
        original_documents_receive_date:
          sales_contract_commercial_value.original_documents_receive_date != null
            ? moment(sales_contract_commercial_value.original_documents_receive_date, "YYYY-MM-DD")
            : null,
        documents_handover_date:
          sales_contract_commercial_value.documents_handover_date != null
            ? moment(sales_contract_commercial_value.documents_handover_date, "YYYY-MM-DD")
            : null,
        etd_board_date: sales_contract_commercial_value.etd_board_date
          ? moment(sales_contract_commercial_value.etd_board_date, "YYYY-MM-DD")
          : null,
        eta: sales_contract_commercial_value.eta ? moment(sales_contract_commercial_value.eta, "YYYY-MM-DD") : null,
        berthing_date:
          sales_contract_commercial_value.berthing_date != null
            ? moment(sales_contract_commercial_value.berthing_date, "YYYY-MM-DD")
            : null,
        unstuffing_date:
          sales_contract_commercial_value.unstuffing_date != null
            ? moment(sales_contract_commercial_value.unstuffing_date, "YYYY-MM-DD")
            : null,
        delivery_date:
          sales_contract_commercial_value.delivery_date != null
            ? moment(sales_contract_commercial_value.delivery_date, "YYYY-MM-DD")
            : null,
        inhouse_date:
          sales_contract_commercial_value.inhouse_date != null
            ? moment(sales_contract_commercial_value.inhouse_date, "YYYY-MM-DD")
            : null,
        bill_entry_date:
          sales_contract_commercial_value.bill_entry_date != null
            ? moment(sales_contract_commercial_value.bill_entry_date, "YYYY-MM-DD")
            : null,
        factory_receive_confirmation_date:
          sales_contract_commercial_value.factory_receive_confirmation_date != null
            ? moment(sales_contract_commercial_value.factory_receive_confirmation_date, "YYYY-MM-DD")
            : null,
        courier_date: sales_contract_commercial_value.courier_date
          ? moment(sales_contract_commercial_value.courier_date, "YYYY-MM-DD")
          : null,
        ud_date:
          sales_contract_commercial_value.ud_date != null
            ? moment(sales_contract_commercial_value.ud_date, "YYYY-MM-DD")
            : null,

        // Conditionally add ud_date if position_type is FOC or FOC Courier
        ...((sales_contract_commercial_value.position_type === "FOC" ||
          sales_contract_commercial_value.position_type === "FOC Courier") && {
          udn: sales_contract_commercial_value.udn,
          ud_date:
            sales_contract_commercial_value.ud_date != null
              ? moment(sales_contract_commercial_value.ud_date, "YYYY-MM-DD")
              : null,
        }),
        goods_description: sales_contract_commercial_value?.description,
        doc_send_date: sales_contract_commercial_value?.doc_send_date
          ? moment(sales_contract_commercial_value?.doc_send_date, "YYYY-MM-DD")
          : null,
        note: sales_contract_commercial_value?.note || "",
      });

      // Track if initial note exists and is one of the auto-generated values
      const noteValue = sales_contract_commercial_value?.note || "";
      const autoGeneratedNotes = [
        "No need MRR due to print",
        "No need MRR due to wash",
        "No need MRR due to embroidery",
      ];
      if (autoGeneratedNotes.includes(noteValue)) {
        setHasInitialNote(true);
      } else {
        setHasInitialNote(false);
      }

      // Set default values for FOC Courier in Foreign supplier type (override any existing values)
      if (supplierType === "foreign" && sales_contract_commercial_value?.position_type === "FOC Courier") {
        form.setFieldsValue({
          shipment_mode: "AIR",
          loading_point: "AIRPORT",
        });
      }

      // Set selected goods description for conditional rendering
      if (sales_contract_commercial_value?.description) {
        setSelectedGoodsDescription(sales_contract_commercial_value?.description);
      }

      const savedCourierName = sales_contract_commercial_value?.forwarder_name;
      if (savedCourierName) {
        setCourierNameList((prev) => (prev.includes(savedCourierName) ? prev : [...prev, savedCourierName]));
      }

      setFileList(
        permitInfo?.sales_contract_commercial?.detail_document.map((item) => {
          return {
            id: item?.id,
            name: item?.document_name,
            document: item?.document,
            // name: file.name,
            path: item?.document,
          };
        }),
      );
    } else {
    }
  }, [permitInfo]);

  const getAcceptanceDetails = async (bodyData) => {
    let response = await getData(GET_LC_ACCEPTANCE_DETAIL, false, bodyData);

    if (response && response?.status === 200) {
      setDisableButton(response?.data?.data || false);
    } else {
      alertPop("error", response?.messages ? response?.messages : "Something Went Wrong!");
    }
  };

  return (
    <Spin spinning={!!loading}>
      <div className="containt-body pt-3">
        <Form layout="vertical" form={form} name="control-hooks" onFinish={onFinishSalesContract}>
        <Card>
          <Row gutter={6}>
            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Type of Shipment"
                name="position_type"
                rules={[
                  {
                    required: true,
                    message: "Type is required",
                  },
                ]}
              >
                <Select
                  placeholder="Type of Shipment"
                  style={{ width: "100%" }}
                  onChange={(value) => {
                    getLcNumber(value);
                    setSelectedType(value);
                    // Reset fields when switching types
                    if (value !== "FOC Courier") {
                      form.resetFields(["sales_contract_number", "buyer_name", "udn", "ud_date"]);
                    }
                    // Reset shipment_mode and loading_point when changing type
                    form.resetFields(["shipment_mode", "loading_point"]);

                    // Set defaults for FOC Courier in foreign supplier
                    if (supplierType === "foreign" && value === "FOC Courier") {
                      form.setFieldsValue({
                        shipment_mode: "AIR",
                        loading_point: "AIRPORT",
                      });
                    }
                  }}
                  disabled={view || disableField}
                  size="small"
                >
                  <option value="LC">LC</option>
                  <option value="RTGS">RTGS</option>
                  <option value="FOC">FOC Bulk</option>
                  <option value="FOC Courier">{supplierType === "epz" ? "FOC Sample" : "FOC Courier"}</option>
                  <option value="TT">TT</option>
                </Select>
              </Form.Item>
            </Col>

            {isFocCourierForeign && (
              <>
                <Col className="gutter-row" span={4}>
                  <Form.Item label="Courier Name" name="forwarder_name">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select or add courier name"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        String(option?.children || "")
                          .toLowerCase()
                          .includes(String(input).toLowerCase())
                      }
                      disabled={view || disableField}
                      size="small"
                      style={{ width: "100%" }}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <Divider style={{ margin: "8px 0" }} />
                          <Space style={{ padding: "0 8px 4px", display: "flex" }}>
                            <Input
                              placeholder="Enter new courier name"
                              value={newCourierNameInput}
                              onChange={(e) => setNewCourierNameInput(e.target.value)}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addCourierName();
                                }
                              }}
                              size="small"
                            />
                            <Button
                              type="text"
                              icon={<PlusOutlined />}
                              onClick={addCourierName}
                              loading={addingCourierName}
                              disabled={!newCourierNameInput?.trim() || addingCourierName}
                            >
                              Add
                            </Button>
                          </Space>
                        </>
                      )}
                    >
                      {courierNameList.map((name) => (
                        <Option key={name} value={name}>
                          {name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item
                    label="Courier Number"
                    name="bl_number"
                    rules={[
                      {
                        required: true,
                        message: "Courier number is required",
                      },
                    ]}
                  >
                    <Input
                      className="w-100"
                      disabled={view || disableField}
                      placeholder="Courier Number"
                      size="small"
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {selectedType !== "FOC" && selectedType !== "FOC Courier" && (
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Import LC/RTGS/TT No"
                  name="import_lc_number"
                  rules={[
                    {
                      required: true,
                      message: "LC/RTGS/TT Number is required",
                    },
                  ]}
                >
                  {positionType === "FOC" ? (
                    <Input className="w-100" disabled={view || disableField} />
                  ) : (
                    <Select
                      showSearch
                      placeholder="LC/RTGS/TT Number"
                      style={{ width: "100%" }}
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                      onChange={(value) => {
                        getLcDetails(value);
                      }}
                      disabled={view || disableField}
                      size="small"
                    >
                      {b2bLcList.map((option) => {
                        return (
                          <option key={option.id} value={option.id}>
                            {option.b2b_lc_number}
                          </option>
                        );
                      })}
                    </Select>
                  )}
                </Form.Item>
              </Col>
            )}

            {selectedType !== "FOC" && selectedType !== "FOC Courier" && (
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Import LC/RTGS/TT Date"
                  name="import_date"
                  rules={[
                    {
                      required: true,
                      message: "Import LC/RTGS/TT Date is required",
                    },
                  ]}
                >
                  {positionType === "FOC" ? (
                    <CustomDatePicker style={{ width: "100%" }} disabled={view || disableField} size="small" />
                  ) : (
                    <CustomDatePicker style={{ width: "100%" }} disabled={true} size="small" />
                  )}
                </Form.Item>
              </Col>
            )}
            <Col className="gutter-row" span={4}>
              <Form.Item label="Sales Contract Number" name="sales_contract_number">
                <Select
                  showSearch
                  placeholder="Sales Contract"
                  style={{ width: "100%" }}
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  onSelect={(value) => getBuyerInfo(value)}
                  disabled={view || !(selectedType === "FOC" || selectedType === "FOC Courier") || disableField}
                  size="small"
                >
                  {salesContractList?.map((option) => {
                    return (
                      <option key={option?.id} value={option?.id}>
                        {option?.reference_no}
                      </option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Supplier Name"
                name="supplier_id"
                rules={[
                  {
                    required: true,
                    message: "Please input Supplier Name!",
                  },
                ]}
              >
                <Select
                  placeholder="Supplier Name"
                  style={{ width: "100%" }}
                  disabled={(selectedType !== "FOC" && selectedType !== "FOC Courier") || disableField}
                  size="small"
                  showSearch
                  filterOption={(input, option) =>
                    option && option.children && option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  optionFilterProp="children"
                >
                  {suppliersList.map((option) => (
                    <Option key={option.id} value={option.id}>
                      {option.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item label="Buyer Name" name="buyer_name">
                <Input className="w-100" disabled={true} placeholder="Buyer Name" size="small" />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item label="UD Number" name="udn">
                <Input
                  className="w-100"
                  disabled={(selectedType !== "FOC" && selectedType !== "FOC Courier") || disableField}
                  placeholder="Utilization Declaration Number"
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item label="UD Date" name="ud_date" placeholder="Utilization Declaration Date">
                <CustomDatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  disabled={(selectedType !== "FOC" && selectedType !== "FOC Courier") || disableField}
                  selected={moment()}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Invoice Number"
                name="invoice_number"
                rules={
                  isFocCourierForeign
                    ? []
                    : [
                        {
                          required: true,
                          message: "Invoice Number is required",
                        },
                      ]
                }
              >
                <Input className="w-100" disabled={view || disableField} placeholder="Invoice Number" size="small" />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Invoice Value"
                name="invoice_value"
                rules={[
                  {
                    required: true,
                    message: "Invoice Value is required",
                  },
                ]}
              >
                <InputNumber
                  disabled={view || disableField}
                  placeholder="Invoice Number"
                  style={{ width: "100%" }}
                  formatter={(value) => commaSeparateNumber(value)}
                  precision={2}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Description of Goods"
                name="goods_description"
                rules={
                  supplierType === "local"
                    ? [
                        {
                          required: true,
                          message: "Description of Goods is required",
                        },
                      ]
                    : []
                }
              >
                <Select
                  placeholder="Description of Goods"
                  style={{ width: "100%" }}
                  disabled={view || disableField}
                  size="small"
                  onChange={(value) => {
                    setSelectedGoodsDescription(value);
                    const goodsId = Number(value);
                    if ([1, 2, 3].includes(goodsId) && supplierType === "local") {
                      form.setFieldValue("inhouse_date", null);
                    }
                    if (supplierType === "local") {
                      if (value === 4) {
                        // Print
                        form.setFieldValue("note", "No need MRR due to print");
                        setHasInitialNote(true);
                      } else if (value === 5) {
                        // Washing
                        form.setFieldValue("note", "No need MRR due to wash");
                        setHasInitialNote(true);
                      } else if (value === 6) {
                        // Embroidery
                        form.setFieldValue("note", "No need MRR due to embroidery");
                        setHasInitialNote(true);
                      } else {
                        form.setFieldValue("note", "");
                        setHasInitialNote(false);
                      }
                    }
                  }}
                >
                  {goodsDescription?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {supplierType !== "local" && (
              <>
                <Col className="gutter-row" span={4}>
                  <Form.Item label="Import Quantity" name="quantity_ctn">
                    <InputNumber
                      className="w-100"
                      disabled={view}
                      placeholder="Import Quantity"
                      style={{ width: "100%" }}
                      formatter={(value) => commaSeparateNumber(value)}
                      precision={2}
                      size="small"
                    />
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item label="Import Quantity Unit" name="import_quantity_unit">
                    <Select placeholder="Import Quantity Unit" style={{ width: "100%" }} disabled={view} size="small">
                      <option value="Cartons">Cartons</option>
                      <option value="Rolls">Rolls</option>
                      <option value="Bales">Bales</option>
                      <option value="Packages">Packages</option>
                      <option value="Pallets">Pallets</option>s<option value="As per Invoice">As per Invoice</option>
                    </Select>
                  </Form.Item>
                </Col>
              </>
            )}

            {/* Hide for EPZ Supplier */}
            {supplierType !== "local" && supplierType !== "epz" && selectedType !== "FOC Courier" && (
              <>
                <Col className="gutter-row" span={4}>
                  <Form.Item label="Invoice Quantity" name="quantity_yds">
                    <InputNumber
                      className="w-100"
                      disabled={view}
                      placeholder="Invoice Quantity"
                      style={{ width: "100%" }}
                      formatter={(value) => commaSeparateNumber(value)}
                      precision={2}
                      size="small"
                    />
                  </Form.Item>
                </Col>

                <Col className="gutter-row" span={4}>
                  <Form.Item label="Invoice Quantity Unit" name="invoice_quantity_unit">
                    <Select placeholder="Invoice Quantity Unit" style={{ width: "100%" }} disabled={view} size="small">
                      <option value="Yds">Yds</option>
                      <option value="Mtr">Mtr</option>
                      <option value="Pcs">Pcs</option>
                      <option value="Cone">Cone</option>
                      <option value="GG">GG</option>
                      <option value="GRS">GRS</option>
                      <option value="KG">KG</option>
                      <option value="As per Invoice">As per Invoice</option>
                    </Select>
                  </Form.Item>
                </Col>
              </>
            )}

            {supplierType !== "local" && (
              <Col className="gutter-row" span={4}>
                <Form.Item label="Gross Weight" name="gross_weight">
                  <Input className="w-100" disabled={view} placeholder="Gross Weight" size="small" />
                </Form.Item>
              </Col>
            )}

            {supplierType !== "local" &&
              supplierType !== "epz" &&
              supplierType === "foreign" &&
              selectedType !== "FOC Courier" && (
                <>
                  <Col className="gutter-row" span={4}>
                    <Form.Item label="CBM" name="cbm">
                      <Input className="w-100" disabled={view} placeholder="CBM" size="small" />
                    </Form.Item>
                  </Col>
                  <Col className="gutter-row" span={4}>
                    <Form.Item label="Forwarder / Courier Name" name="forwarder_name">
                      <Input className="w-100" disabled={view} placeholder="Forwarder / Courier Name" size="small" />
                    </Form.Item>
                  </Col>

                  <Col className="gutter-row" span={4}>
                    <Form.Item label="Vessel / Flight Name" name="vessel_name">
                      <Input className="w-100" disabled={view} placeholder="Vessel / Flight Name" size="small" />
                    </Form.Item>
                  </Col>
                </>
              )}
            {!isFocCourierForeign && (
            <Col className="gutter-row" span={4}>
              <Form.Item
                label={
                  selectedType === "FOC Courier" && supplierType !== "epz"
                    ? "Courier number"
                    : supplierType === "local"
                      ? "TR/CHALAN No"
                      : "BL/AWBL/TR/CHALAN No"
                }
                name="bl_number"
                rules={[
                  {
                    required: true,
                    message:
                      selectedType === "FOC Courier" && supplierType !== "epz"
                        ? "Courier number is required"
                        : "BL / AWBL / TR / CHALLAN Number is required",
                  },
                ]}
              >
                <Input
                  className="w-100"
                  disabled={view || disableField}
                  placeholder={
                    selectedType === "FOC Courier" && supplierType !== "epz"
                      ? "Courier number"
                      : "BL / AWBL / TR / CHALLAN Number"
                  }
                  size="small"
                />
              </Form.Item>
            </Col>
            )}

            {supplierType !== "local" && supplierType !== "epz" && selectedType !== "FOC Courier" && (
              <>
                <Col className="gutter-row" span={4}>
                  <Form.Item label="Container / Flight Number" name="container_number">
                    <Input className="w-100" disabled={view} placeholder="Container / Flight Number" size="small" />
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item label="Courier Number" name="courier_number">
                    <Input className="w-100" disabled={view} placeholder="Courier Number" size="small" />
                  </Form.Item>
                </Col>
              </>
            )}
            {supplierType !== "local" && supplierType !== "epz" && (
              <>
                <Col className="gutter-row" span={4}>
                  <Form.Item label="ETD/On Board Date" name="etd_board_date">
                    <CustomDatePicker style={{ width: "100%" }} disabled={view} size="small" />
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item label="ETA Date" name="eta">
                    <CustomDatePicker style={{ width: "100%" }} disabled={view} size="small" />
                  </Form.Item>
                </Col>
              </>
            )}

            {supplierType !== "local" && supplierType !== "epz" && selectedType !== "FOC Courier" && (
              <>
                <Col className="gutter-row" span={4}>
                  <Form.Item label="Berthing Date" name="berthing_date">
                    <CustomDatePicker style={{ width: "100%" }} disabled={view} size="small" />
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item label="Un-Stuffing Date" name="unstuffing_date">
                    <CustomDatePicker style={{ width: "100%" }} disabled={view} size="small" />
                  </Form.Item>
                </Col>
              </>
            )}

            <Col className="gutter-row" span={4}>
              <Form.Item label="Origianl Doc Receive Date" name="original_documents_receive_date">
                <CustomDatePicker
                  style={{ width: "100%" }}
                  disabled={
                    view || (supplierType === "local" && !hasPermission(LOCAL_INVOICE_DOC_SEND_RECEIVE_DATA_PERMISSION))
                  }
                  size="small"
                />
              </Form.Item>
            </Col>
            {selectedType !== "FOC Courier" && (
              <Col className="gutter-row" span={4}>
                <Form.Item label="Doc Send/Hand over Date" name="documents_handover_date">
                  <CustomDatePicker
                    style={{ width: "100%" }}
                    disabled={
                      view ||
                      (supplierType === "local" && !hasPermission(LOCAL_INVOICE_DOC_SEND_RECEIVE_DATA_PERMISSION))
                    }
                    size="small"
                  />
                </Form.Item>
              </Col>
            )}
            {selectedGoodsDescription &&
              ![1, 2, 3].includes(Number(selectedGoodsDescription)) && (
                <Col className="gutter-row" span={4}>
                  <Form.Item label="Inhouse Date" name="inhouse_date">
                    <CustomDatePicker style={{ width: "100%" }} disabled={view} size="small" />
                  </Form.Item>
                </Col>
              )}

            {/* Doc Send Date and Note fields for Local supplier type */}
            {supplierType === "local" &&
              selectedGoodsDescription &&
              selectedGoodsDescription !== 1 &&
              selectedGoodsDescription !== 2 &&
              selectedGoodsDescription !== 3 && (
                <>
                  <Col className="gutter-row" span={4}>
                    <Form.Item label="Doc Send Date" name="doc_send_date">
                      <CustomDatePicker style={{ width: "100%" }} disabled={view || disableField} size="small" />
                    </Form.Item>
                  </Col>
                  <Col className="gutter-row" span={4}>
                    <Form.Item label="Note" name="note">
                      <Input.TextArea
                        className="w-100"
                        disabled={view || disableField || hasInitialNote}
                        placeholder="Note"
                        size="small"
                        rows={2}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}

            {supplierType !== "local" && supplierType !== "epz" && (
              <Col className="gutter-row" span={4}>
                <Form.Item label="Mode of Shipment" name="shipment_mode">
                  <Select
                    placeholder="Mode of Shipment"
                    style={{ width: "100%" }}
                    size="small"
                    disabled={view || (supplierType === "foreign" && selectedType === "FOC Courier")}
                  >
                    <option value="SEA">SEA</option>
                    <option value="AIR">AIR</option>
                    <option value="LAND">LAND</option>
                    <option value="TRANSPORT">TRANSPORT</option>
                  </Select>
                </Form.Item>
              </Col>
            )}
            {supplierType !== "local" && selectedType !== "FOC Courier" && (
              <Col className="gutter-row" span={4}>
                <Form.Item label="Shipment Methods" name="shipment_type">
                  <Select placeholder="Shipment Methods" style={{ width: "100%" }} size="small" disabled={view}>
                    <option value="LCL">LCL</option>
                    <option value="FCL">FCL</option>
                    <option value="LTL">LTL</option>
                    <option value="FTL">FTL</option>
                    <option value="ACF">ACF</option>
                    <option value="ASF">ASF</option>
                    <option value="Others">Others</option>
                  </Select>
                </Form.Item>
              </Col>
            )}

            {supplierType !== "local" && (
              <Col className="gutter-row" span={4}>
                <Form.Item label="Delivery Date" name="delivery_date">
                  <CustomDatePicker
                    style={{ width: "100%" }}
                    disabled={view}
                    size="small"
                    onChange={(date) => {
                      // For FOC Courier, sync with Projected In-House Date
                      if (selectedType === "FOC Courier" && date) {
                        form.setFieldValue("inhouse_date", date);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            )}

            {supplierType !== "local" &&
              (!selectedGoodsDescription || [1, 2, 3].includes(Number(selectedGoodsDescription))) && (
                <Col className="gutter-row" span={4}>
                  <Form.Item label="Projected in-House Date" name="inhouse_date">
                    <CustomDatePicker
                      style={{ width: "100%" }}
                      disabled={view || selectedType === "FOC Courier"}
                      size="small"
                      onChange={(date) => {
                        // For FOC Courier, sync with Delivery Date
                        if (selectedType === "FOC Courier" && date) {
                          form.setFieldValue("delivery_date", date);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
            {supplierType !== "local" && (
              <>
                <Col className="gutter-row" span={4}>
                  <Form.Item label="Clearing Agent" name="clearing_agent">
                    <Select placeholder="Clearing Agent" style={{ width: "100%" }} size="small" disabled={view}>
                      <option value="Self C&F">Self C&F</option>
                      <option value="C&F Vendor">C&F Vendor</option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col className="gutter-row" span={4}>
                  <Form.Item label="Loading Point" name="loading_point">
                    <Select
                      placeholder="Loading Point"
                      style={{ width: "100%" }}
                      size="small"
                      disabled={view || (supplierType === "foreign" && selectedType === "FOC Courier")}
                    >
                      <option value="CTG">CTG</option>
                      <option value="AIRPORT">AIRPORT</option>
                      <option value="AEPZ">AEPZ</option>
                      <option value="DEPZ">DEPZ</option>
                      <option value="CEPZ">CEPZ</option>
                      <option value="Benapole">Benapole</option>
                      <option value="Others">Others</option>
                    </Select>
                  </Form.Item>
                </Col>
              </>
            )}

            <Form.Item name="sales_contract_id" type="hidden">
              <Input
                className="w-100"
                disabled={true}
                style={{ width: "100%", color: "#000" }}
                type="hidden"
                size="small"
              />
            </Form.Item>
          </Row>
        </Card>
      </Form>
      <ImportDetailDocument
        DOC_ADD={IMPORT_DETAIL_ADD_DOC}
        setFileList={setFileList}
        filesList={filesList}
        view={view}
        disableUpdateButton={disableUpdateButton}
        disableField={disableField}
      />
      </div>
    </Spin>
  );
});

export default AddExportSalesContractForm;


